// frontend/src/components/FinanceReport/Accounting/components/accounts/company-accounts/ProfitAllocationFormModal.jsx

import { useEffect, useState } from "react";
import Modal from "../../../../../UI/Modal";
import Dropdown from "../../../../../UI/Dropdown";
import {
  createProfitAllocation,
  patchProfitAllocation,
  uploadProfitAllocationDocument,
  deleteProfitAllocationDocument,
} from "./requests";

const TYPE_OPTIONS = [
  { id: "income", label: "Приход" },
  { id: "expense", label: "Расход" },
  { id: "dividends", label: "Дивиденды" },
];

const STATUS_OPTIONS = [
  { id: "PENDING", label: "Ожидает" },
  { id: "PAID", label: "Получено" },
];

const ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp";
const today = () => new Date().toISOString().slice(0, 10);

const emptyForm = () => ({
  type: "income",
  amount: "",
  date: today(),
  payment_agent: "",
  comment: "",
  payment_status: "PENDING",
});

export default function ProfitAllocationFormModal({
  open,
  mode,
  initial,
  accountId,
  currencyId,
  onClose,
  onSaved,
}) {
  const [form, setForm] = useState(emptyForm());
  const [files, setFiles] = useState([]);
  const [documentsToDelete, setDocumentsToDelete] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    setFiles([]);
    setDocumentsToDelete([]);

    if (mode === "edit" && initial) {
      setForm({
        type: initial.type || "income",
        amount: String(initial.amount ?? ""),
        date: initial.date || today(),
        payment_agent: initial.payment_agent || "",
        comment: initial.comment || "",
        payment_status: initial.payment_status || "PENDING",
      });
    } else {
      setForm(emptyForm());
    }
  }, [open, mode, initial]);

  const existingDocuments = initial?.documents || [];
  const visibleDocuments = existingDocuments.filter(
    (d) => !documentsToDelete.includes(d.id)
  );

  const setField = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleAddFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length === 0) return;
    setFiles((prev) => {
      const keys = new Set(prev.map((f) => `${f.name}::${f.size}`));
      return [...prev, ...picked.filter((f) => !keys.has(`${f.name}::${f.size}`))];
    });
    e.target.value = "";
  };

  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));
  const markForDeletion = (id) =>
    setDocumentsToDelete((prev) => (prev.includes(id) ? prev : [...prev, id]));
  const unmarkForDeletion = (id) =>
    setDocumentsToDelete((prev) => prev.filter((x) => x !== id));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const amountNum = Number(form.amount);
    if (!amountNum || amountNum <= 0) {
      setError("Сумма должна быть больше нуля.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        type: form.type,
        account: accountId,
        amount: String(amountNum),
        currency: currencyId ?? null,
        date: form.date || null,
        payment_agent: form.payment_agent || "",
        comment: form.comment || "",
        payment_status: form.payment_status || "PENDING",
      };

      let saved;
      if (mode === "edit" && initial) {
        saved = await patchProfitAllocation(initial.id, payload);
      } else {
        saved = await createProfitAllocation(payload);
      }

      const errors = [];

      for (const file of files) {
        try {
          await uploadProfitAllocationDocument(saved.id, file);
        } catch (err) {
          errors.push(`Файл "${file.name}": ${err.message}`);
        }
      }

      for (const docId of documentsToDelete) {
        try {
          await deleteProfitAllocationDocument(docId);
        } catch (err) {
          errors.push(`Документ #${docId}: ${err.message}`);
        }
      }

      await onSaved({ warning: errors.length > 0 ? errors.join("\n") : null });
    } catch (err) {
      setError(err.message || "Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={open}
      onClose={() => !saving && onClose()}
      title={mode === "edit" && initial ? "Редактировать" : "Добавить транзакцию"}
    >
      <form onSubmit={handleSubmit} className="space-y-3">
        {error && (
          <div className="whitespace-pre-wrap rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <Dropdown
          value={form.type}
          options={TYPE_OPTIONS}
          getLabel={(o) => o.label}
          onChange={(v) => setField("type", v)}
        />

        <input
          type="number"
          className="w-full border p-2 rounded-xl"
          placeholder="Сумма"
          value={form.amount}
          onChange={(e) => setField("amount", e.target.value)}
          onWheel={(e) => e.currentTarget.blur()}
          min="0.01"
          step="0.01"
          required
        />

        <input
          type="date"
          className="w-full border p-2 rounded-xl"
          value={form.date}
          onChange={(e) => setField("date", e.target.value)}
        />

        <input
          className="w-full border p-2 rounded-xl"
          placeholder="Платёж через"
          value={form.payment_agent}
          onChange={(e) => setField("payment_agent", e.target.value)}
        />

        <input
          className="w-full border p-2 rounded-xl"
          placeholder="Комментарий"
          value={form.comment}
          onChange={(e) => setField("comment", e.target.value)}
        />

        <Dropdown
          value={form.payment_status}
          options={STATUS_OPTIONS}
          getLabel={(o) => o.label}
          onChange={(v) => setField("payment_status", v)}
        />

        <div className="rounded-xl border border-gray-200 p-3 space-y-2">
          <p className="text-sm font-semibold text-gray-700">📎 Документы</p>

          {visibleDocuments.length > 0 && (
            <div className="space-y-1">
              {visibleDocuments.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between gap-2 rounded-lg bg-gray-50 px-2 py-1 text-xs"
                >
                  <a
                    href={doc.file_url || doc.file}
                    target="_blank"
                    rel="noreferrer"
                    className="truncate text-blue-700 hover:underline"
                  >
                    {doc.original_name || `Документ #${doc.id}`}
                  </a>
                  <button
                    type="button"
                    onClick={() => markForDeletion(doc.id)}
                    className="shrink-0 text-red-600 hover:bg-red-50 rounded px-1.5 py-0.5"
                  >
                    🗑
                  </button>
                </div>
              ))}
            </div>
          )}

          {documentsToDelete.length > 0 && (
            <div className="space-y-1 rounded-lg border border-red-200 bg-red-50 p-2">
              <p className="text-[11px] font-medium text-red-700">
                Будет удалено при сохранении ({documentsToDelete.length}):
              </p>
              {existingDocuments
                .filter((d) => documentsToDelete.includes(d.id))
                .map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between gap-2 text-xs">
                    <span className="truncate text-red-700 line-through">
                      {doc.original_name || `#${doc.id}`}
                    </span>
                    <button
                      type="button"
                      onClick={() => unmarkForDeletion(doc.id)}
                      className="shrink-0 text-red-700 hover:bg-white rounded px-1.5 py-0.5"
                    >
                      ↩
                    </button>
                  </div>
                ))}
            </div>
          )}

          {files.length > 0 && (
            <div className="space-y-1">
              {files.map((file, i) => (
                <div
                  key={`${file.name}-${i}`}
                  className="flex items-center justify-between gap-2 rounded-lg bg-blue-50 px-2 py-1 text-xs"
                >
                  <span className="truncate text-slate-700">
                    ⬆ {file.name}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeFile(i)}
                    className="shrink-0 text-slate-500 hover:bg-slate-100 rounded px-1.5 py-0.5"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}

          <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50">
            ⬆ Добавить файлы
            <input
              type="file"
              multiple
              accept={ACCEPT}
              onChange={handleAddFiles}
              className="hidden"
            />
          </label>
        </div>

        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={() => !saving && onClose()}
            disabled={saving}
            className="flex-1 border border-gray-300 rounded-full py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Отмена
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex-1 bg-black text-white py-2 rounded-full text-sm font-semibold hover:bg-gray-800 disabled:opacity-50"
          >
            {saving ? "Сохранение…" : mode === "edit" ? "Сохранить" : "Добавить"}
          </button>
        </div>
      </form>
    </Modal>
  );
}